import React, { useState, useEffect } from 'react';

/**
 * Modal bloqueante de Autorización para el Tratamiento de Datos Personales
 * (Habeas Data — Ley 1581 de 2012, Decreto 1377 de 2013 - Colombia).
 *
 * Se muestra la primera vez que un usuario entra a la aplicación.
 * No se puede cerrar con Escape ni clic fuera — el usuario debe decidir.
 *
 * Props:
 *   - isOpen: boolean
 *   - onAccept: () => Promise<void>  (registra aceptación en backend)
 *   - onReject: () => void           (cierra sesión)
 *   - userName?: string              (saludo personalizado opcional)
 */
const HabeasDataModal = ({ isOpen, onAccept, onReject, userName }) => {
  const [leido, setLeido] = useState(false);
  const [loading, setLoading] = useState(false);

  // Bloquear scroll del body mientras el modal está abierto
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAccept = async () => {
    if (!leido || loading) return;
    setLoading(true);
    try {
      await onAccept();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] flex flex-col">

        {/* Header azul */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-2xl px-4 sm:px-6 py-4 flex items-center gap-3 shrink-0">
          <div className="bg-white/20 p-2 rounded-lg text-2xl shrink-0">🔒</div>
          <div className="min-w-0">
            <h2 className="text-base sm:text-xl font-bold leading-tight">
              Autorización para el Tratamiento de Datos Personales
            </h2>
            <p className="text-xs text-blue-100 mt-0.5">
              Ley 1581 de 2012 — República de Colombia
            </p>
          </div>
        </div>

        {/* Body scrolleable */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-5 text-sm text-gray-700 space-y-3">
          {userName && (
            <p className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-blue-900 font-medium">
              Hola, <strong>{userName}</strong>. Antes de continuar, por favor lee y acepta el siguiente tratamiento de datos personales:
            </p>
          )}

          <p>
            De conformidad con lo establecido en la <strong>Ley 1581 de 2012</strong> y el{' '}
            <strong>Decreto 1377 de 2013</strong> de la República de Colombia sobre Protección de
            Datos Personales, autorizo de manera libre, expresa, voluntaria e informada a{' '}
            <strong>IMEC — Informes Mensuales de Ejecución Contractual</strong> (en adelante,
            "la Aplicación"), una aplicación desarrollada y operada por un proveedor independiente,{' '}
            <strong>no afiliada al SENA</strong> ni a ninguna entidad pública o privada, para el
            tratamiento de mis datos personales bajo los siguientes términos:
          </p>

          <h3 className="font-bold text-blue-900 mt-4">1. Responsable del Tratamiento</h3>
          <p>
            La Aplicación IMEC, operada por: <strong>Ingeniero Wilkin Julio</strong>
            <br />
            📧 Contacto:{' '}
            <a href="mailto:wilking10@gmail.com" className="text-blue-600 hover:underline font-medium">
              wilking10@gmail.com
            </a>
          </p>

          <h3 className="font-bold text-blue-900 mt-4">2. Datos que se recolectan</h3>
          <p>
            Nombre completo, correo electrónico, cédula de ciudadanía, lugar de expedición, cargo,
            número de contrato, planilla SOI, evidencias documentales (imágenes/PDFs) cargadas
            voluntariamente por el usuario, firma digital y demás información requerida para el
            funcionamiento de la aplicación.
          </p>

          <h3 className="font-bold text-blue-900 mt-4">3. Finalidad del Tratamiento</h3>
          <p>Los datos recolectados serán utilizados <strong>exclusivamente</strong> para:</p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li>Permitir el acceso y uso personal del usuario a la herramienta IMEC.</li>
            <li>Generar los documentos PDF (cuentas de cobro, informes mensuales, post-cuentas, evidencias) solicitados por el propio usuario.</li>
            <li>Almacenar de forma segura la información de cada usuario para que pueda consultarla y editarla cuando lo requiera.</li>
          </ul>

          <h3 className="font-bold text-blue-900 mt-4">4. Confidencialidad y NO transferencia a terceros</h3>
          <p>
            La Aplicación <strong>NO comercializa, vende, comparte, transfiere ni cede</strong> los
            datos personales a terceros, empresas externas, entidades gubernamentales, anunciantes
            ni con fines publicitarios. Los datos permanecen únicamente bajo control del usuario y
            del Responsable del Tratamiento para el funcionamiento del servicio.
          </p>

          <h3 className="font-bold text-blue-900 mt-4">5. Almacenamiento</h3>
          <p>
            Los datos se almacenan en servidores con medidas razonables de seguridad. El usuario
            es responsable de la veracidad de la información que suministra.
          </p>

          <h3 className="font-bold text-blue-900 mt-4">6. Derechos del Titular</h3>
          <p>Como titular de los datos, tengo derecho a:</p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li><strong>Conocer, actualizar y rectificar</strong> mis datos en cualquier momento desde la sección "Mi Configuración".</li>
            <li><strong>Solicitar la supresión</strong> de mis datos contactando al administrador del sistema.</li>
            <li><strong>Revocar la autorización</strong> otorgada, lo cual implicará la eliminación de mi cuenta y mis datos asociados.</li>
            <li>Conocer el uso que se le ha dado a mis datos personales.</li>
          </ul>
          <p className="text-xs text-gray-500 italic">
            Para ejercer estos derechos contactar a:{' '}
            <a href="mailto:wilking10@gmail.com" className="text-blue-600 hover:underline">
              wilking10@gmail.com
            </a>
          </p>

          <h3 className="font-bold text-blue-900 mt-4">7. Naturaleza voluntaria</h3>
          <p>
            La autorización es voluntaria. Si <strong>no acepto</strong> el tratamiento de mis
            datos, <strong>no podré acceder ni utilizar la Aplicación</strong>.
          </p>
        </div>

        {/* Footer fijo: checkbox + botones */}
        <div className="border-t bg-gray-50 px-4 sm:px-6 py-4 rounded-b-2xl space-y-3 shrink-0">
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={leido}
              onChange={(e) => setLeido(e.target.checked)}
              disabled={loading}
              className="mt-0.5 accent-blue-600 w-4 h-4 cursor-pointer"
            />
            <span>
              He <strong>leído y entiendo</strong> los términos del tratamiento de datos personales.
            </span>
          </label>
          <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
            <button
              type="button"
              onClick={onReject}
              disabled={loading}
              className="w-full sm:w-auto px-5 py-2.5 bg-white border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 font-medium transition disabled:opacity-50"
            >
              No acepto
            </button>
            <button
              type="button"
              onClick={handleAccept}
              disabled={!leido || loading}
              className="w-full sm:w-auto px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Procesando...
                </>
              ) : (
                <>✓ Acepto</>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default HabeasDataModal;
